// @ts-nocheck

import { DataPoint } from "@/models/sharedModels";
import {
  PatternScoreHistory,
  ScaleScoreHistory,
} from "@dimensional-engineering/dimensional-models";

function createDailyDateArrayForChartOptions(days: number): number[] {
  const dates: number[] = [];
  const now = new Date();

  for (let i = 0; i < days; i++) {
    const currentDate = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i)
    );
    dates.push(currentDate.getTime());
  }

  return dates.reverse(); // Ensure the array is in chronological order
}

export function createDateArrayFromTimestampToNow(
  startTimestamp: number
): number[] {
  const dates: number[] = [];
  const startDate = new Date(startTimestamp);
  const now = new Date();

  for (
    let d = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate()
      )
    );
    d <= now;
    d.setUTCDate(d.getUTCDate() + 1)
  ) {
    dates.push(d.getTime());
  }

  return dates;
}

export function createThreeMonthDateArrayForChartOptions(): number[] {
  return createDailyDateArrayForChartOptions(90);
}

export function createSixMonthDateArrayForChartOptions(): number[] {
  return createDailyDateArrayForChartOptions(180);
}

export function createOneYearDateArrayForChartOptions(): number[] {
  return createDailyDateArrayForChartOptions(365);
}

export function createThreeYearDateArrayForChartOptions(): number[] {
  return createDailyDateArrayForChartOptions(365 * 3);
}

export function plotScoresForDaysList(
  scoreHistory: ScaleScoreHistory | PatternScoreHistory,
  xAxisArray: number[]
): DataPoint[] {
  if (!scoreHistory.historicScores) return [];

  const getMidnightUTC = (timestamp: number) => {
    const date = new Date(timestamp);
    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
  };

  //Filter to keep only the latest per UTC day
  const filtered = scoreHistory.historicScores.filter((current, _, all) => {
    const currentTime = new Date(current.createdOn).getTime();
    const currentMidnight = getMidnightUTC(currentTime);

    // Find the latest item for that same day
    const latestForDay = all.reduce((latest, candidate) => {
      const candidateTime = new Date(candidate.createdOn).getTime();
      const candidateMidnight = getMidnightUTC(candidateTime);

      if (candidateMidnight !== currentMidnight) return latest;
      if (!latest) return candidate;

      return new Date(candidate.createdOn).getTime() >
        new Date(latest.createdOn).getTime()
        ? candidate
        : latest;
    }, null as typeof current | null);

    return current === latestForDay;
  });

  //map to midnight
  const scoresMappedToMidnightValue = filtered.map((h) => ({
    ...h,
    createdOn: new Date(
      Date.UTC(
        new Date(h.createdOn).getUTCFullYear(),
        new Date(h.createdOn).getUTCMonth(),
        new Date(h.createdOn).getUTCDate()
      )
    ).getTime(),
  }));

  const returnValue: DataPoint[] = [];

  let currentScore = null;

  for (let i = 0; i < xAxisArray.length; i++) {
    const currentXaxisArrayValue = xAxisArray[i];
    const found = scoresMappedToMidnightValue.find(
      (h) => h.createdOn === currentXaxisArrayValue
    );
    if (found && found.percentScore != null) {
      returnValue.push({
        name: found.createdOn.toString(),
        value: [found.createdOn, Number((found.percentScore * 100).toFixed(0))],
      });
      currentScore = found.percentScore;
    }
  } //the above loops through the x-axis and finds matching dates to plot them

  // Ensure an initial zero value if missing
  if (returnValue.length > 0 && returnValue[0].value[0] !== xAxisArray[0]) {
    returnValue.push({
      name: xAxisArray[0].toString(),
      value: [xAxisArray[0], 0],
    });
  }

  // Push one more value: first found.createdOn - 1 day
  if (returnValue.length > 0) {
    const firstCreatedOn = returnValue[0].value[0];
    const oneDayBefore = firstCreatedOn - 24 * 60 * 60 * 1000;
    returnValue.push({
      name: oneDayBefore.toString(),
      value: [oneDayBefore, 0],
    });
  }

  const sortedReturnValue = returnValue.sort((a, b) => a.value[0] - b.value[0]);

  // Get the last plotted date and its score.
  const lastPlottedDate =
    sortedReturnValue[sortedReturnValue.length - 1]?.value[0];
  const lastPlottedScore =
    sortedReturnValue[sortedReturnValue.length - 1]?.value[1];

  // Find the index of the last plotted date in the xAxisArray.
  const lastPlottedIndex = xAxisArray.findIndex(
    (day) => day === lastPlottedDate
  );

  // For every remaining day after the last plotted date, fill in with the last score.
  for (let i = lastPlottedIndex + 1; i < xAxisArray.length; i++) {
    // Check if the day is already plotted to avoid duplicates.
    if (!sortedReturnValue.find((dp) => dp.value[0] === xAxisArray[i])) {
      sortedReturnValue.push({
        name: xAxisArray[i].toString(),
        value: [xAxisArray[i], lastPlottedScore],
      });
    }
  }

  return sortedReturnValue.sort((a, b) => a.value[0] - b.value[0]);
}
